import { useState } from "react";

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handle}
      title="Copy transcript"
      className="p-1 rounded-lg text-[#555] hover:text-white hover:bg-[#2e2e2e] transition-colors"
    >
      {copied
        ? <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
        : <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" strokeLinecap="round" strokeLinejoin="round"/><path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      }
    </button>
  );
}

function VideoIcon() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
        <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
  );
}

function MediaTile({ file, index, onSelect }) {
  const isVideo = file.media_type === "video";
  const filename = file.path.split("/").pop();

  return (
    <button
      onClick={() => onSelect(file, index)}
      className="
        relative aspect-square rounded-xl overflow-hidden
        bg-[#1a1a1a] border border-[#2e2e2e]
        hover:border-indigo-500 hover:scale-[1.02]
        transition-all duration-200 group
      "
    >
      {isVideo ? (
        <>
          <video
            src={`file://${file.path}`}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
          />
          <VideoIcon />
        </>
      ) : (
        <img
          src={`file://${file.path}`}
          alt={`media-${index}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}

      {/* Index badge */}
      <span className="absolute top-2 left-2 text-xs bg-black/70 text-white px-2 py-0.5 rounded-full">
        {isVideo ? "▶ Video" : "Photo"} {index + 1}
      </span>

      {/* Filename on hover */}
      <div className="
        absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent
        px-3 py-3 translate-y-full group-hover:translate-y-0 transition-transform
      ">
        <p className="text-xs text-gray-300 truncate">{filename}</p>
      </div>
    </button>
  );
}

function VideoModal({ file, transcript, onClose }) {
  if (!file) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#1a1a1a] rounded-2xl overflow-hidden flex flex-col md:flex-row max-w-5xl w-full max-h-[90vh] border border-[#2e2e2e]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video */}
        <div className="flex-1 bg-black flex items-center">
          <video
            src={`file://${file.path}`}
            controls
            autoPlay
            className="w-full max-h-[60vh] md:max-h-[90vh]"
          />
        </div>

        {/* Transcript panel */}
        <div className="w-full md:w-96 flex flex-col border-t md:border-t-0 md:border-l border-[#2e2e2e]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2e2e2e] shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">Transcript</h3>
              {transcript?.language && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs">
                  {transcript.language.toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Copy button */}
              {transcript?.transcript && (
                <CopyBtn text={transcript.transcript} />
              )}
              <button
                onClick={onClose}
                className="p-1 rounded-lg text-[#555] hover:text-white hover:bg-[#2e2e2e] transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Full scrollable transcript */}
          <div className="flex-1 overflow-y-auto p-5">
            {transcript?.transcript ? (
              <p className="text-sm text-gray-300 leading-7 whitespace-pre-wrap tracking-wide">
                {transcript.transcript}
              </p>
            ) : transcript?.error ? (
              <p className="text-sm text-red-400">{transcript.error}</p>
            ) : (
              <p className="text-sm text-[#555] italic">No speech detected in this video.</p>
            )}
          </div>

          {/* Word count footer */}
          {transcript?.transcript && (
            <div className="px-5 py-2 border-t border-[#2e2e2e] shrink-0">
              <p className="text-xs text-[#444]">
                {transcript.transcript.trim().split(/\s+/).length.toLocaleString()} words
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MediaGallery({ files, transcripts }) {
  const [selected, setSelected] = useState(null);

  if (!files || files.length === 0) return null;

  const transcriptFor = (file) =>
    transcripts?.find((t) => t.file === file.path) || null;

  const handleSelect = (file) => {
    if (file.media_type === "video") setSelected(file);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">
        Media{" "}
        <span className="text-sm font-normal text-[#555]">
          ({files.length} file{files.length !== 1 ? "s" : ""})
        </span>
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {files.map((file, i) => (
          <MediaTile
            key={file.path}
            file={file}
            index={i}
            onSelect={handleSelect}
          />
        ))}
      </div>

      <VideoModal
        file={selected}
        transcript={selected ? transcriptFor(selected) : null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
